import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable, forkJoin, throwError } from 'rxjs';
import { API_ENDPOINTS } from '../config/api-endpoints';
import { QRCode } from '../models/qr.model';
import { Page } from '../models';

export interface QrCodeStatistics {
  totalQRCodes: number;
  activeQRCodes: number;
  totalScanCount: number;
  typeCounts: {
    TABLE?: number;
    PROMOTION?: number;
    EVENT?: number;
    FEEDBACK?: number;
    PAYMENT?: number;
    CONTACT?: number;
    LOYALTY?: number;
    MENU?: number;
  };
}

export interface AvailableTablesResponse {
  totalTables: number;
  availableTables: number;
  message: string;
}

export interface QrValidationResponse {
  valid: boolean;
  qrCode: string;
  barId: string;
  type: string;
  tableNumber: string;
  message: string;
}

export interface TableInfoResponse {
  qrCode: string;
  barId: string;
  tableNumber: string;
  tableStatus: string;
  message: string;
}

export interface CreateQRCodeRequest {
  tableNumber: string;
  type?: string;
  barId?: string;
}

export interface BulkQRCodeRequest {
  startTable: number;
  endTable: number;
  type?: string;
  barId?: string;
}

@Injectable({
  providedIn: 'root'
})
export class QrCodeService {

  constructor(private http: HttpClient) { }

  getQrCodes(): Observable<QRCode[]> {
    return this.http.get<Page<QRCode>>(`qr:${API_ENDPOINTS.QR.CODES}`)
      .pipe(map(response => response.content));
  }

  getQrCodeById(id: number | string): Observable<QRCode> {
    return this.http.get<QRCode>(`qr:${API_ENDPOINTS.QR.CODE_DETAIL(id)}`);
  }

  getQrCodeByData(qrData: string): Observable<QRCode> {
    return this.http.get<QRCode>(`qr:${API_ENDPOINTS.QR.SCAN_INFO(qrData)}`);
  }

  getStatistics(): Observable<QrCodeStatistics> {
    return this.http.get<QrCodeStatistics>(`qr:${API_ENDPOINTS.QR.STATISTICS}`);
  }

  getAvailableTables(barId?: string): Observable<AvailableTablesResponse> {
    const params: any = {};
    if (barId) params.barId = barId;
    return this.http.get<AvailableTablesResponse>(`qr:${API_ENDPOINTS.QR.AVAILABLE_TABLES}`, { params });
  }

  createQrCode(request: CreateQRCodeRequest): Observable<QRCode> {
    const qrCodeData: QRCode = {
      ...request,
      tableNumber: request.tableNumber,
      type: request.type || 'TABLE',
      barId: request.barId || 'B001',
      qrData: `${request.barId || 'B001'}-${request.tableNumber}-QR`,
      isActive: true
    };
    return this.http.post<QRCode>(`qr:${API_ENDPOINTS.QR.GENERATE}`, qrCodeData);
  }

  /**
   * Generate one QR code per table across a range.
   *
   * This used to POST {startTable, endTable, type} to the single-create
   * endpoint, which expects a whole QRCode entity and answered 400 — so
   * "generate codes for tables 1-20" never produced a single code. The service
   * has no bulk endpoint, but it does have a per-table one, so the range is
   * expanded here and the calls issued together.
   */
  createBulkQrCodes(request: BulkQRCodeRequest): Observable<QRCode[]> {
    const start = Number(request.startTable);
    const end = Number(request.endTable);
    if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) {
      return throwError(() => new Error('Invalid table range'));
    }
    const tables = Array.from({ length: end - start + 1 }, (_, i) => String(start + i));
    return forkJoin(tables.map(t => this.generateTableQrCode(t)));
  }

  generateTableQrCode(tableNumber: string): Observable<QRCode> {
    return this.http.post<QRCode>(`qr:${API_ENDPOINTS.QR.TABLE_QR(tableNumber)}`, null);
  }

  generateMenuQrCode(): Observable<QRCode> {
    return this.http.post<QRCode>(`qr:${API_ENDPOINTS.QR.MENU_QR}`, null);
  }

  updateQrCodeStatus(qrCode: QRCode, newStatus: boolean): Observable<QRCode> {
    const updatedQrCode: QRCode = { ...qrCode, isActive: newStatus };
    return this.http.put<QRCode>(`qr:${API_ENDPOINTS.QR.CODE_DETAIL(qrCode.id!)}`, updatedQrCode);
  }

  deleteQrCode(id: number | string): Observable<void> {
    return this.http.delete<void>(`qr:${API_ENDPOINTS.QR.CODE_DETAIL(id)}`);
  }

  scanQrCode(qrData: string): Observable<QRCode> {
    return this.http.post<QRCode>(`qr:${API_ENDPOINTS.QR.SCAN}`, null, { params: { qrData } });
  }

  validateQrCode(qrCode: string): Observable<QrValidationResponse> {
    return this.http.get<QrValidationResponse>(`qr:${API_ENDPOINTS.QR.VALIDATE(qrCode)}`);
  }

  getTableByQrCode(qrCode: string): Observable<TableInfoResponse> {
    return this.http.get<TableInfoResponse>(`qr:${API_ENDPOINTS.QR.TABLE_INFO(qrCode)}`);
  }

  getQrCodesByTable(tableNumber: string): Observable<QRCode[]> {
    return this.http.get<QRCode[]>(`qr:${API_ENDPOINTS.QR.TABLE_DETAIL(tableNumber)}`);
  }

  getActiveQrCodes(): Observable<QRCode[]> {
    return this.http.get<QRCode[]>(`qr:${API_ENDPOINTS.QR.ACTIVE}`);
  }

  getScannableQrCodes(): Observable<QRCode[]> {
    return this.http.get<QRCode[]>(`qr:${API_ENDPOINTS.QR.SCANNABLE}`);
  }

  getQrCodesByType(type: string): Observable<QRCode[]> {
    return this.http.get<QRCode[]>(`qr:${API_ENDPOINTS.QR.BY_TYPE(type)}`);
  }

  getMostScanned(page: number = 0, size: number = 10): Observable<QRCode[]> {
    return this.http.get<QRCode[]>(`qr:${API_ENDPOINTS.QR.MOST_SCANNED}`, {
      params: { page: page.toString(), size: size.toString() }
    });
  }

  getRecentlyScanned(page: number = 0, size: number = 10): Observable<QRCode[]> {
    return this.http.get<QRCode[]>(`qr:${API_ENDPOINTS.QR.RECENTLY_SCANNED}`, {
      params: { page: page.toString(), size: size.toString() }
    });
  }

  searchQrCodes(filters: {
    qrData?: string;
    type?: string;
    tableNumber?: string;
    isActive?: boolean;
    page?: number;
    size?: number;
  }): Observable<Page<QRCode>> {
    let params = new HttpParams();
    if (filters.qrData) params = params.set('qrData', filters.qrData);
    if (filters.type) params = params.set('type', filters.type);
    if (filters.tableNumber) params = params.set('tableNumber', filters.tableNumber);
    if (filters.isActive != null) params = params.set('isActive', filters.isActive.toString());
    params = params.set('page', (filters.page ?? 0).toString());
    params = params.set('size', (filters.size ?? 20).toString());
    return this.http.get<Page<QRCode>>(`qr:${API_ENDPOINTS.QR.SEARCH}`, { params });
  }
}
